import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiProjectSettingKey,
  ApiYAxisChartType,
} from "@incendium/api";
import { Grid } from "@mui/material";
import { useSelectedProject } from "Hooks";
import { useMemo, useState } from "react";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import { AttributionHeader, BucketsROICharts, useSelectedAttributes } from "features/analytics";
import { AnalyticsSpacing } from "consts";
import withPaidChannelAttributes from "HoC/withPaidChannelAttributes";
import { useTrafficBuckets } from "Hooks/useTrafficBuckets";
import {
  CHART_KEY_ATTRIBUTION_HEADER_TOP_BY_TREND,
  CHART_KEY_PAID_MEDIA_ATTRIBUTION_CAC,
  CHART_KEY_PAID_MEDIA_ATTRIBUTION_CONVERSION_LTV,
  CHART_KEY_PAID_MEDIA_ATTRIBUTION_NEW_CUSTOMER_ACQUISITION,
  CHART_KEY_PAID_MEDIA_ATTRIBUTION_PERFORMANCE_PIE,
  CHART_KEY_PAID_MEDIA_ATTRIBUTION_REVENUE_TABLE,
  CHART_KEY_PAID_MEDIA_ATTRIBUTION_ROI,
  CHART_KEY_PAID_MEDIA_ATTRIBUTION_SALES_COUNT,
  CHART_KEY_PAID_MEDIA_ATTRIBUTION_SESSIONS_ENGAGEMENT,
  CHART_KEY_PAID_MEDIA_ATTRIBUTION_SPEND_PIE,
} from "features/analytics/constants/chartKeys";

interface IAttributionInsightsProps {
  chartKeyScope: string;
  marketingDimension: ApiDimension;
  dimensionTitle: string;
  paidChannelAttributes: ApiChartAttribute[];
}

function AttributionInsights({
  chartKeyScope,
  marketingDimension,
  dimensionTitle,
  paidChannelAttributes,
}: IAttributionInsightsProps) {
  const { loading, rOIBuckets } = useTrafficBuckets();
  const { projectSettings } = useSelectedProject();
  const [lifetimeSelectedMetric, setLifetimeSelectedMetric] =
    useState<ApiMetric>(
      ApiMetric.METRIC_FIRST_VISIT_SESSION_MACRO_CONVERSION_COUNT
    );
  const { selectedAttributes } = useSelectedAttributes();
  const attributes = useMemo(() => {
    return [...paidChannelAttributes, ...selectedAttributes.filter(attribute => ![...paidChannelAttributes.map(attribute => attribute.key)].includes(attribute.key))];
  }, [paidChannelAttributes, selectedAttributes]);

  return (
    <Grid container spacing={AnalyticsSpacing}>
      <Grid item xs={12}>
        <AttributionHeader
          chartKey={`${CHART_KEY_ATTRIBUTION_HEADER_TOP_BY_TREND}_${chartKeyScope}`}
          dimension={marketingDimension}
          title={`Marketing ${dimensionTitle}`}
          attributes={attributes}
        />
      </Grid>

      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={`${CHART_KEY_PAID_MEDIA_ATTRIBUTION_SPEND_PIE}_${chartKeyScope}`}
          chart={{
            name: `Total Spend by ${dimensionTitle}`,
            dimension: [marketingDimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            attributes: attributes,
            includePublisherReadyOnly: true,
            limit: 15,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SPEND],
              },
            ],
            displayOptions: {
              noFilters: true,
              showTotals: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={`${CHART_KEY_PAID_MEDIA_ATTRIBUTION_SALES_COUNT}_${chartKeyScope}`}
          chart={{
            name: `Sales Count by ${dimensionTitle}`,
            dimension: [marketingDimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: attributes,
            limit: 15,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSION_SALES_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSION_SALES_COUNT]: ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              noLegend: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 400 }}>
        <StandardAnalyticsCard
          chartKey={`${CHART_KEY_PAID_MEDIA_ATTRIBUTION_ROI}_${chartKeyScope}`}
          chart={{
            name: `ROI by ${dimensionTitle}`,
            dimension: [marketingDimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: attributes,
            includePublisherReadyOnly: true,
            limit: 15,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_ROI],
                chart: {
                  [ApiMetric.METRIC_ROI]: ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              noLegend: true,
            },
          }}
        />
      </Grid>

      {projectSettings[
        ApiProjectSettingKey.SHOW_AWARENESS_ACQUISTION_RETENTION_INSIGHTS
      ] === "true" &&
        !loading &&
        rOIBuckets.length > 0 && (
          <Grid item xs={12}>
            <BucketsROICharts dimensions={[marketingDimension]} />
          </Grid>
        )}

      <Grid item xs={6} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={`${CHART_KEY_PAID_MEDIA_ATTRIBUTION_SESSIONS_ENGAGEMENT}_${chartKeyScope}`}
          chart={{
            name: `Sessions & Engagement Levels by ${dimensionTitle}`,
            dimension: [marketingDimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_EFFECTIVE_SESSION_RATE],
                chart: {
                  [ApiMetric.METRIC_EFFECTIVE_SESSION_RATE]:
                    ApiYAxisChartType.DOT,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              metricsOrder: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
              ],
            },
          }}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={`${CHART_KEY_PAID_MEDIA_ATTRIBUTION_CONVERSION_LTV}_${chartKeyScope}`}
          chart={{
            name: `${dimensionTitle} Conversion LTV by`,
            dimension: [marketingDimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: attributes,
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [lifetimeSelectedMetric],
                chart: {
                  [ApiMetric.METRIC_FIRST_VISIT_SESSION_MACRO_CONVERSION_COUNT]:
                    ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_THIRTY_DAY_SESSION_MACRO_CONVERSION_COUNT_ADDITIONAL]:
                    ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_SIXTY_DAY_MACRO_CONVERSIONS_COUNT_ADDITIONAL]:
                    ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_NINETY_DAY_MACRO_CONVERSIONS_COUNT_ADDITIONAL]:
                    ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_FIRST_VISIT_SESSION_SALES_COUNT]:
                    ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_THIRTY_DAY_SESSION_SALES_COUNT_ADDITIONAL]:
                    ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_SIXTY_DAY_SESSION_SALES_COUNT_ADDITIONAL]:
                    ApiYAxisChartType.BAR,
                  [ApiMetric.METRIC_NINETY_DAY_SESSION_SALES_COUNT_ADDITIONAL]:
                    ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              metricsOrder: [
                ApiMetric.METRIC_FIRST_VISIT_SESSION_MACRO_CONVERSION_COUNT,
                ApiMetric.METRIC_THIRTY_DAY_SESSION_MACRO_CONVERSION_COUNT_ADDITIONAL,
                ApiMetric.METRIC_SIXTY_DAY_MACRO_CONVERSIONS_COUNT_ADDITIONAL,
                ApiMetric.METRIC_NINETY_DAY_MACRO_CONVERSIONS_COUNT_ADDITIONAL,
                ApiMetric.METRIC_FIRST_VISIT_SESSION_SALES_COUNT,
                ApiMetric.METRIC_THIRTY_DAY_SESSION_SALES_COUNT_ADDITIONAL,
                ApiMetric.METRIC_SIXTY_DAY_SESSION_SALES_COUNT_ADDITIONAL,
                ApiMetric.METRIC_NINETY_DAY_SESSION_SALES_COUNT_ADDITIONAL,
              ],
              availableMetrics: [
                ApiMetric.METRIC_FIRST_VISIT_SESSION_MACRO_CONVERSION_COUNT,
                ApiMetric.METRIC_FIRST_VISIT_SESSION_SALES_COUNT,
              ],
            },
          }}
          metricsOverride={
            lifetimeSelectedMetric ===
              ApiMetric.METRIC_FIRST_VISIT_SESSION_MACRO_CONVERSION_COUNT
              ? [
                ApiMetric.METRIC_FIRST_VISIT_SESSION_MACRO_CONVERSION_COUNT,
                ApiMetric.METRIC_THIRTY_DAY_SESSION_MACRO_CONVERSION_COUNT_ADDITIONAL,
                ApiMetric.METRIC_SIXTY_DAY_MACRO_CONVERSIONS_COUNT_ADDITIONAL,
                ApiMetric.METRIC_NINETY_DAY_MACRO_CONVERSIONS_COUNT_ADDITIONAL,
              ]
              : [
                ApiMetric.METRIC_FIRST_VISIT_SESSION_SALES_COUNT,
                ApiMetric.METRIC_THIRTY_DAY_SESSION_SALES_COUNT_ADDITIONAL,
                ApiMetric.METRIC_SIXTY_DAY_SESSION_SALES_COUNT_ADDITIONAL,
                ApiMetric.METRIC_NINETY_DAY_SESSION_SALES_COUNT_ADDITIONAL,
              ]
          }
          onChangeMetric={setLifetimeSelectedMetric}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={`${CHART_KEY_PAID_MEDIA_ATTRIBUTION_PERFORMANCE_PIE}_${chartKeyScope}`}
          chart={{
            name: `${dimensionTitle} Performance by`,
            dimension: [marketingDimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            limit: 20,
            attributes: attributes,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSION_SALES_COUNT],
              },
            ],
            displayOptions: {
              noFilters: true,
              showTotals: true,
              availableMetrics: [
                ApiMetric.METRIC_SESSION_SALES_COUNT,
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_PAGEVIEWS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_PAGEVIEWS_COUNT,
                ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
                ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
              ],
            },
          }}
        />
      </Grid>

      <Grid item xs={8} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={`${CHART_KEY_PAID_MEDIA_ATTRIBUTION_REVENUE_TABLE}_${chartKeyScope}`}
          chart={{
            name: `${dimensionTitle} Revenue Performance`,
            dimension: [marketingDimension],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: attributes,
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_REVENUE,
                  ApiMetric.METRIC_SALES_COUNT,
                  ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
                  ApiMetric.METRIC_ROI,
                  ApiMetric.METRIC_SPEND,
                  ApiMetric.METRIC_LTV,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={4} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={`${CHART_KEY_PAID_MEDIA_ATTRIBUTION_CAC}_${chartKeyScope}`}
          chart={{
            name: `CAC by ${dimensionTitle}`,
            dimension: [marketingDimension],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: attributes,
            limit: 10,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_CAC],
                chart: {
                  [ApiMetric.METRIC_CAC]: ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Grid>
      <Grid item xs={8} sx={{ minHeight: 450 }}>
        <StandardAnalyticsCard
          chartKey={`${CHART_KEY_PAID_MEDIA_ATTRIBUTION_NEW_CUSTOMER_ACQUISITION}_${chartKeyScope}`}
          chart={{
            name: `New Customer Acquisition Performance`,
            dimension: [marketingDimension],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: attributes,
            limit: 10,
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_CPNV,
                  ApiMetric.METRIC_CAC,
                  ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
                  ApiMetric.METRIC_AVERAGE_SALE_VALUE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              rowsPerPage: 5,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default withPaidChannelAttributes(AttributionInsights);
